import type { FirebaseApp } from 'firebase/app'
import type { AppCheck } from 'firebase/app-check'
import type { Auth } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'
import { setSessionCookie, clearSessionCookie } from '~/utils/sessionCookie'
import { clearPerUserClientState } from '~/utils/perUserClientState'
import { reloadOnAccountSwitch } from '~/utils/accountSwitch'
import { isMarketingPath } from '~/utils/marketingRoutes'
import { firebaseClientOptions } from '~/utils/firebaseClientOptions'
import { apiErrorCode, API_ERROR_CODES } from '#shared/apiErrorCodes'

function getAuthEmulatorOrigin(configuredOrigin?: string) {
  if (configuredOrigin) return configuredOrigin

  return window.location.origin
}

/**
 * Firebase is loaded via dynamic import so the SDK stays out of the entry
 * chunk. On non-marketing routes (login, /app/**, /addin, …) the plugin awaits
 * `ensureFirebaseAuth()` before completing setup; on marketing routes it starts
 * `ensureFirebaseAuth()` in the background without awaiting so hydration is not
 * blocked while IndexedDB is checked for an existing Firebase session.
 *
 * `$waitForAuth()` awaits the first `onIdTokenChanged` settlement (after any
 * session cookie exchange completes) and is the single mechanism used by
 * `useAuth().waitForAuth()`, `auth.global.ts`, `default.vue`, and `research.vue`.
 */
/** True in a frame whose parent is another origin; reading a cross-origin parent's location throws. */
function framedByAnotherOrigin(): boolean {
  if (window === window.parent) return false
  try {
    return window.parent.location.origin !== window.location.origin
  } catch {
    return true
  }
}

/**
 * A User Timing mark for the return-to-the-app path (MAR-1421), readable in the browser's Performance
 * panel or with `performance.getEntriesByType('mark')`, so how long auth takes to settle is measured
 * rather than guessed.
 */
function markAuthTiming(name: string): void {
  try {
    performance.mark(name)
  } catch {
    // No User Timing API: there is nothing to record.
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  const config = useRuntimeConfig()

  interface AppUser {
    displayName: string | null
    email: string | null
    uid: string
  }

  const user = useState<AppUser | null>('auth-user', () => null)
  const loading = useState<boolean>('auth-loading', () => true)

  let appPromise: Promise<FirebaseApp> | null = null
  let appCheckPromise: Promise<AppCheck | null> | null = null
  let authPromise: Promise<Auth> | null = null
  let anonPromise: Promise<Auth> | null = null
  let dbPromise: Promise<Firestore> | null = null

  let resolveAuthReady!: () => void
  const authReadyPromise = new Promise<void>((resolve) => {
    resolveAuthReady = resolve
  })

  const firebaseConfig = firebaseClientOptions(config.public)

  function ensureFirebaseApp(): Promise<FirebaseApp> {
    appPromise ??= (async () => {
      const { initializeApp } = await import('firebase/app')
      return initializeApp(firebaseConfig)
    })()
    return appPromise
  }

  /**
   * Initialize Firebase App Check (`ReCaptchaEnterpriseProvider`, invisible score-based) ON DEMAND
   * (MAR-1214). Nothing initializes it at page load: every initialization costs a reCAPTCHA
   * assessment, so it starts only when something needs a token — a request to a gated route
   * (`APP_CHECK_ROUTES` in `#shared/appCheck`), the one retry after a 401 `app_check_required`
   * (a new account's first `users` row), or a warm-up from a surface about to need one (sign-up,
   * `/research`). Skipped under the local emulator (`firebaseEmulator`) or when no site key is
   * configured. Never fires a `/api` call on its own.
   */
  function ensureAppCheck(): Promise<AppCheck | null> {
    const siteKey = (config.public.recaptchaEnterpriseSiteKey as string | undefined)?.trim()
    if (config.public.firebaseEmulator || !siteKey) {
      return Promise.resolve(null)
    }
    appCheckPromise ??= (async () => {
      try {
        const [app, appCheckMod] = await Promise.all([
          ensureFirebaseApp(),
          import('firebase/app-check'),
        ])
        return appCheckMod.initializeAppCheck(app, {
          provider: new appCheckMod.ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true,
        })
      } catch (err) {
        console.warn('[app-check] Failed to initialize Firebase App Check:', err)
        return null
      }
    })()
    return appCheckPromise
  }

  /** `limitedUse` mints a single-use token for a route the server consumes (`{ consume: true }`). */
  async function getAppCheckToken(options?: { limitedUse?: boolean }): Promise<string | null> {
    const appCheck = await ensureAppCheck()
    if (!appCheck) return null
    try {
      const appCheckMod = await import('firebase/app-check')
      const result = options?.limitedUse
        ? await appCheckMod.getLimitedUseToken(appCheck)
        : await appCheckMod.getToken(appCheck, false)
      return result.token || null
    } catch (err) {
      console.warn('[app-check] Failed to obtain App Check token:', err)
      return null
    }
  }

  /**
   * Ensure an ANONYMOUS session on the main app, for the public /research page.
   *
   * There is ONE Firebase app. A logged-out visitor who runs research owns their
   * `public_rag_jobs` doc via an anonymous session on this same app, and presents its
   * ID token as the Bearer the `/api/public/rag/*` endpoints verify. The app still
   * treats them as logged-out: `onIdTokenChanged` maps an anonymous user to
   * `user.value = null` with no `__session` cookie, and `getToken()` withholds
   * anonymous tokens from the authed API. Crucially, a real sign-in REPLACES the
   * anonymous user and sign-out clears it — so the anon identity never outlives an
   * auth transition, with no separate app to evict. The server
   * (`server/middleware/auth.ts` rejects anonymous everywhere but `/api/public/*`)
   * is the authoritative boundary; this is its client side.
   */
  function ensureAnonAuth(): Promise<Auth> {
    anonPromise ??= (async () => {
      try {
        const auth = await ensureFirebaseAuth()
        // Await restore before checking currentUser, else a persisted session races and
        // we'd mint a second anon uid. Reuse any live session (real or anon); only mint
        // an anonymous one when there is genuinely none.
        await auth.authStateReady()
        if (!auth.currentUser) {
          const { signInAnonymously } = await import('firebase/auth')
          await signInAnonymously(auth)
        }
        return auth
      } finally {
        anonPromise = null
      }
    })()
    return anonPromise
  }

  function ensureFirebaseAuth(): Promise<Auth> {
    authPromise ??= (async () => {
      // Only arm the loading gate when SSR did not already seed a verified user,
      // avoiding a hydration mismatch when SSR rendered with `auth-loading = false`.
      if (!user.value) {
        loading.value = true
      }
      try {
        const [app, authMod] = await Promise.all([ensureFirebaseApp(), import('firebase/auth')])
        // Use sessionStorage in a frame inside ANOTHER origin (the Word add-in) to avoid cross-origin
        // localStorage issues; otherwise prefer browserLocalPersistence ahead of indexedDBLocalPersistence
        // so existing IndexedDB sessions migrate to localStorage on boot without registering IndexedDB's
        // pagehide/visibilitychange listeners that throw "Database is closing/hidden" (MAR-1234). A
        // frame inside Marcus itself (the plan step's hosted fields, MAR-1317) shares the parent's
        // storage and must see its signed-in user, so it is treated like the top window.
        const auth = authMod.initializeAuth(app, {
          popupRedirectResolver: authMod.browserPopupRedirectResolver,
          persistence: framedByAnotherOrigin()
            ? authMod.browserSessionPersistence
            : [
                authMod.browserLocalPersistence,
                authMod.indexedDBLocalPersistence,
                authMod.browserSessionPersistence,
              ],
        })

        if (config.public.firebaseEmulator) {
          authMod.connectAuthEmulator(auth, getAuthEmulatorOrigin(config.public.firebaseAuthEmulatorOrigin), { disableWarnings: true })
        }

        // onIdTokenChanged fires on login/logout AND token refresh (~hourly),
        // keeping the __session cookie fresh. (Moved here from useAuth so the
        // subscription is bound exactly once, when the SDK actually loads.)
        //
        // Anonymous users are NOT app users: an anonymous session (minted by
        // ensureAnonAuth for the public /research page) reads as logged-out — null
        // `user.value`, no `__session` cookie — so the navbar and auth.global redirect
        // correctly, and getToken() withholds its token from the authed API. We do NOT
        // sign it out: it legitimately owns a `public_rag_jobs` doc. A real sign-in
        // replaces it and sign-out clears it, so it never outlives an auth transition.
        // The server enforces the same boundary (server/middleware/auth.ts).
        authMod.onIdTokenChanged(auth, async (fbUser) => {
          // What this tab was showing before the event, for `reloadOnAccountSwitch` (MAR-1408).
          const previousUid = user.value?.uid ?? null
          try {
            if (fbUser && !fbUser.isAnonymous) {
              /**
               * A RESTORED or refreshed session for the account this tab already shows (or the first
               * one it sees) passes the auth gate NOW; the cookie POST below finishes in the
               * background (MAR-1421). Waiting for it held the whole app behind "Comprobando
               * sesión…" on every return after the one-hour cookie lapsed: a token refresh, the
               * POST's MFA read, claims reconcile and audit, and sometimes a forced second refresh.
               * Nothing reads data on the strength of the cookie alone: every API call sends its own
               * Bearer token, which the server verifies (MFA included, AlexAmin/marcus#899), and an
               * MFA_REQUIRED answer to the POST still signs the user out below. This reverses
               * MAR-1122's AC8 for this path (decided by Alex, 2026-10-28). Explicit sign-in still
               * waits for the cookie, in `useAuth().finalizeSignInUser`.
               *
               * An account SWITCH keeps waiting: the reload it triggers re-renders on the server from
               * the cookie, which must already name the new account (MAR-1408).
               */
              if (!previousUid || previousUid === fbUser.uid) {
                user.value = {
                  displayName: fbUser.displayName,
                  email: fbUser.email,
                  uid: fbUser.uid,
                }
                loading.value = false
                markAuthTiming('marcus:auth:user-restored')
                resolveAuthReady()
              }
              try {
                const idToken = await fbUser.getIdToken()
                const claimsRefreshed = await setSessionCookie(idToken)
                markAuthTiming('marcus:auth:session-cookie-set')
                user.value = {
                  displayName: fbUser.displayName,
                  email: fbUser.email,
                  uid: fbUser.uid,
                }
                // The server just minted this user's org identity into their Firebase custom claims
                // (MAR-427). The token we posted predates them, so pull a new one NOW rather than
                // waiting up to an hour for Firebase's own refresh — otherwise a user who has just
                // created their organization keeps presenting a token that says they have none.
                //
                // This re-enters onIdTokenChanged with the new token, which posts the cookie again;
                // that second call finds nothing to mint and reports false, so it terminates.
                if (claimsRefreshed) await fbUser.getIdToken(true)
                // Another tab signed a DIFFERENT account in: this tab's profile, sidebar and route
                // gate still describe the old one (MAR-1408).
                reloadOnAccountSwitch(previousUid, fbUser.uid)
              } catch (err) {
                /**
                 * `MFA_REQUIRED` (AlexAmin/marcus#899) — THE case this whole subscription exists to
                 * catch: `fbUser` is Firebase's PERSISTED session, restored from IndexedDB on reload or
                 * refreshed silently ~hourly, and neither of those is a new "sign in" that would trigger
                 * Firebase's own MFA challenge. A session that predates the user enrolling MFA (or one
                 * that simply never completed a second factor) reaches here forever otherwise.
                 * `signOut()` clears `user.value`, clears Firebase's persisted session, and
                 * hard-navigates to `/login`, where signing back in runs into Identity Platform's
                 * native TOTP challenge.
                 */
                if (apiErrorCode(err) === API_ERROR_CODES.mfaRequired) {
                  await useAuth().signOut()
                  return
                }
                user.value = {
                  displayName: fbUser.displayName,
                  email: fbUser.email,
                  uid: fbUser.uid,
                }
                console.error('[auth] Failed to get ID token:', err)
              }
            } else {
              const hadSession = user.value !== null
              user.value = null
              if (hadSession) {
                // Signed out somewhere else, or the session ended: the same forgetting as
                // `useAuth().signOut()` (MAR-1458). The reload below resets in-memory state.
                clearPerUserClientState()
                await clearSessionCookie()
              }
              // Signed out from another tab: leave the previous account's workspace on screen no longer.
              reloadOnAccountSwitch(previousUid, null)
            }
          } finally {
            loading.value = false
            resolveAuthReady()
          }
        })

        /**
         * A tab that was in the background missed Firebase's hourly token refresh, and with it the
         * `__session` re-mint. Asking for the token when the tab is shown again refreshes it if it
         * has expired (a no-op otherwise), which re-enters `onIdTokenChanged` above and re-posts
         * the cookie before the user's next full page load needs it (MAR-1421). A discarded tab
         * reloads instead, and is covered by the session hint and the fast path above.
         */
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return
          const current = auth.currentUser
          if (!current || current.isAnonymous) return
          void current.getIdToken().catch(() => undefined)
        })

        nuxtApp.provide('firebaseAuth', auth)
        return auth
      } catch (err) {
        authPromise = null
        loading.value = false
        resolveAuthReady()
        throw err
      }
    })()
    return authPromise
  }

  function ensureFirebaseDb(): Promise<Firestore> {
    dbPromise ??= (async () => {
      const [app, fsMod] = await Promise.all([ensureFirebaseApp(), import('firebase/firestore')])
      // Firestore for the public-RAG queue (`public_rag_jobs`). The queue lives in a
      // NAMED database (e.g. `gemma-queue`), not `(default)`, so target it explicitly
      // — must match server-side getDb()/FIREBASE_FIRESTORE_DATABASE_ID.
      const dbId = config.public.firebaseFirestoreDatabaseId as string | undefined
      const db: Firestore = dbId ? fsMod.getFirestore(app, dbId) : fsMod.getFirestore(app)

      if (config.public.firebaseEmulator) {
        const fsHost = config.public.firebaseFirestoreEmulatorHost as string | undefined
        if (fsHost) {
          const [host, port] = fsHost.split(':')
          fsMod.connectFirestoreEmulator(db, host || '127.0.0.1', Number(port) || 8080)
        }
      }

      nuxtApp.provide('firebaseDb', db)
      return db
    })()
    return dbPromise
  }

  async function waitForAuth(): Promise<void> {
    await ensureFirebaseAuth().catch(() => {})
    await authReadyPromise
  }

  if (isMarketingPath(window.location.pathname)) {
    // Marketing route: start Firebase Auth initialization in the background
    // without blocking hydration so the CTA spinner resolves once IndexedDB
    // has been checked.
    void ensureFirebaseAuth().catch((err) => {
      console.error('[auth] Background Firebase Auth init failed:', err)
    })
  } else {
    // Non-marketing route: eagerly init Firebase Auth before route middleware
    // or components run.
    await ensureFirebaseAuth()
  }

  return {
    provide: {
      ensureFirebaseAuth,
      ensureFirebaseDb,
      ensureAnonAuth,
      ensureAppCheck,
      getAppCheckToken,
      waitForAuth,
    },
  }
})
